/**
 * ADR-118 — adapter interface.
 *
 * Decouples the provider from any specific backend. SimpleJWT (AIM /
 * SMS / AIR), Django-allauth (ONE), and future backends all plug in
 * via the same interface.
 *
 * Optional methods (`loginWithToken`, `loginWithOAuth`, `loginWithPasskey`)
 * let an adapter advertise which login paths it supports. The
 * provider's `useAuthActions()` hook only exposes the actions whose
 * adapter methods are defined — apps that don't have passkey support
 * (AIM/SMS today) never see a `loginWithPasskey` action.
 *
 * Adapters are responsible for:
 *   - Normalizing their backend-specific response shapes into the
 *     canonical `LoginResult<U>` (`from @aegix/auth-core`).
 *   - Translating HTTP errors into a consistent `AuthError` so
 *     consumers don't have to know which backend they hit.
 *   - NOT touching localStorage / cookies / WS — that's the
 *     provider's job. Adapters are pure HTTP-layer indirection.
 */

import type { LoginResult, LogoutResult, BaseUser } from '@aegix/auth-core';

/**
 * Credentials for email + password login. Adapters may extend with
 * backend-specific fields (e.g. allauth's `signup` flow takes
 * additional name fields), but `email` and `password` are the
 * shared core.
 */
export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Credentials for OAuth-style social login. The `provider` string
 * is adapter-defined (e.g. `'google' | 'apple' | 'microsoft'` for
 * allauth).
 *
 * Two mutually-exclusive flows are supported:
 *
 *   - **Popup / embedded ID-token flow** — caller already holds the
 *     platform-issued ID token (Google Sign-In's `credential.idToken`,
 *     Apple's response token, Microsoft MSAL token). Pass `idToken`
 *     (plus `nonce` if required by the provider).
 *
 *   - **Redirect-callback flow** — the SPA bounces to the provider,
 *     the provider redirects back with a query-string dict
 *     (`{code, state, scope, ...}`). Pass that dict as `params`;
 *     the adapter forwards it verbatim as the allauth `token` field.
 *
 * Exactly one of `idToken` or `params` must be provided.
 */
export interface OAuthCredentials {
  provider: string;
  /** Popup/embedded flow: platform-issued ID token. */
  idToken?: string;
  /** Optional nonce for Microsoft id_token verification. */
  nonce?: string;
  /** Redirect-callback flow: full query-string dict from the provider. */
  params?: Record<string, string>;
}

/**
 * Errors surfaced by adapters. Adapters MUST throw these (or
 * subclasses) rather than raw `Error` so consumers can pattern-match
 * on `kind` to decide UI treatment.
 */
export class AuthError extends Error {
  constructor(
    message: string,
    public readonly kind:
      | 'invalid_credentials'
      | 'account_locked'
      | 'account_inactive'
      | 'requires_verification'
      | 'network'
      | 'server'
      | 'unknown',
    /** Backend-supplied detail, if any. */
    public readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Login adapter. Every adapter implements `login` (email+password
 * is the universal lowest common denominator) and `logout`. The
 * other login methods are optional — an adapter advertises support
 * by defining the method.
 */
export interface LoginAdapter<U extends BaseUser = BaseUser> {
  /** Email + password login. Universal. */
  login(credentials: LoginCredentials): Promise<LoginResult<U>>;

  /**
   * Token-based login. Used by SSO popup return flows where the JWT
   * arrives via URL parameter. The adapter is responsible for
   * fetching the full user shape (`/me/`) using the token.
   * Optional — apps without SSO don't need this.
   */
  loginWithToken?(jwt: string): Promise<LoginResult<U>>;

  /**
   * Silent cross-app SSO bootstrap (ADR-118 Phase 2). No
   * password: trades the parent-domain, **HttpOnly** `aegix-sso` assertion —
   * sent automatically via `credentials: 'include'`, never read by JS — for a
   * full `LoginResult` INCLUDING a per-app `refresh_token` (the durable
   * anchor). Called by the provider's boot path on a genuine first-landing
   * (no access token AND no stored refresh token) BEFORE the cookie-probe,
   * replacing the identity-ambiguous cookie-probe with a correct-identity SSO
   * handoff. Optional — apps/backends without the `aegix-sso` assertion keep
   * today's cookie-probe fallback.
   */
  bootstrapFromSso?(): Promise<LoginResult<U>>;

  /**
   * OAuth / social login. Adapter dispatches based on `provider`.
   * Optional — only ONE today.
   */
  loginWithOAuth?(credentials: OAuthCredentials): Promise<LoginResult<U>>;

  /**
   * Passkey / WebAuthn. The adapter runs the WebAuthn ceremony
   * via `navigator.credentials.get(...)` internally; consumers
   * just call `loginWithPasskey()`. Optional — only ONE today.
   */
  loginWithPasskey?(): Promise<LoginResult<U>>;

  /**
   * Self-registration with email verification. Returns either a
   * full `LoginResult` (auto-login) OR a `requiresVerification: true`
   * sentinel that the UI handles by routing to an email-check page.
   * Optional — only ONE today.
   */
  signup?(credentials: SignupCredentials): Promise<LoginResult<U> | RequiresVerification>;

  /**
   * Server-side authoritative logout. SimpleJWT adapter POSTs to
   * `/api/v2/auth/logout/`; allauth uses its own logout
   * endpoint. Always called by the provider's `logout()` action,
   * BEFORE local state clears, so a 5xx here doesn't block the
   * client-side cleanup (the provider catches and logs).
   */
  logout(jwt: string | null): Promise<LogoutResult>;

  /**
   * Re-establish state at boot (the reconcile probe). Adapter
   * calls its `/me/`-style endpoint and returns the full user shape.
   * The provider dispatches `login_success` with the result; if
   * `fetchMe` throws, the provider dispatches `logout` + clears the cookie.
   *
   * `token` is the access token from storage, if any. A
   * token-based adapter (SimpleJWT) sends it as `Authorization: JWT
   * <token>` so the probe authenticates with the credential that
   * actually works in-session — the `jwt-auth` cookie is host-only and
   * isn't readable/sent whenever the app host ≠ API host (split-origin
   * local, dev).
   *
   * The allauth adapter uses `token` too, but differently — under
   * the **app** client it reads the `session_key` claim off the JWT and
   * replays it as `X-Session-Token` (there is no `sessionid` cookie to
   * fall back on). Only under the **browser** client does it rely purely
   * on `credentials: 'include'`. **No adapter simply ignores `token`** —
   * assume you must use it.
   */
  fetchMe(token?: string | null): Promise<U>;

  /**
   * Sync user language preference to the backend.
   *
   * `token` is the access token and it is **required** in
   * practice. Unlike the other methods on this interface, implementations
   * hit a regular API endpoint (`/api/v2/user/:id/`) rather than the
   * adapter's own auth surface, so they must send
   * `Authorization: JWT <token>` — the platform scheme is `JWT`, not
   * `Bearer`. Cookie-only auth 401s wherever the app client is enabled,
   * and an allauth `X-Session-Token` is no substitute: the edge only
   * forwards that header on `/_allauth/*`.
   */
  updateLanguage?: (
    userId: string | number,
    language: string,
    token?: string | null,
  ) => Promise<U>;
}

/**
 * Sentinel for self-registration paths that require email
 * verification before login completes. The UI routes to the
 * "check your email" screen on this value.
 */
export interface RequiresVerification {
  requiresVerification: true;
  email: string;
}

export function isRequiresVerification(
  v: unknown,
): v is RequiresVerification {
  return (
    typeof v === 'object' &&
    v !== null &&
    (v as RequiresVerification).requiresVerification === true
  );
}

export interface SignupCredentials extends LoginCredentials {
  first_name?: string;
  last_name?: string;
  cell_phone?: string;
}

/**
 * Refresh adapter. Distinct from `LoginAdapter` because:
 *   - Some adapters don't support refresh (allauth uses session
 *     cookies + an automatic-rotation flow inside its own client)
 *   - Apps may want different refresh-token-lifetimes than
 *     login-token-lifetimes, and decoupling makes that explicit
 */
export interface RefreshAdapter {
  /**
   * Trade a refresh token for a fresh access token. Optionally
   * returns a bumped `permissionsVersion` — if it changes vs.
   * previous, the provider invalidates cached perms and re-fetches.
   */
  refresh(refreshToken: string): Promise<{
    jwt: string;
    permissionsVersion?: number;
  }>;
}
